/**
 * Singleton WebSocket manager for notifications.
 *
 * Lifecycle:
 *   - connect() is called once after login (from authStore.setAuth)
 *   - stays alive across page navigations (no React dependency)
 *   - reconnects with exponential backoff on unexpected close
 *   - disconnect() is called on logout (from authStore.logout)
 *
 * React components subscribe to state via useNotificationSocket() hook
 * which reads from Zustand notificationStore — no useEffect WebSocket deps.
 */

import { useNotificationStore } from '@/stores/notificationStore'
import { queryKeys } from '@/api/query-keys'
import { NOTIFICATION_ROUTES } from '@/lib/notification-routes'
import { getValidAccessToken } from '@/lib/auth-token'
import { toast } from 'sonner'
import type { Notification } from '@/types'

let ws: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0
let currentToken: string | null = null
let isIntentionalClose = false

const MAX_RETRIES = 15
const BASE_DELAY = 1000
const MAX_DELAY = 30000

function getWsUrl(token: string): string {
  const wsBase = import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000'
  return `${wsBase}/ws/notifications/${token}/`
}

function handleMessage(event: MessageEvent) {
  try {
    const parsed = JSON.parse(event.data)
    // WhatsApp voice calls ride this socket because it is the one open on
    // every page; the call engine loads lazily on the first call event.
    if (typeof parsed.type === 'string' && parsed.type.startsWith('call.') && parsed.call) {
      import('@/features/inbox/calls/call-engine').then(({ handleCallEvent }) =>
        handleCallEvent(parsed.type, parsed.call),
      )
      return
    }
    if (parsed.type === 'notification.new' && parsed.notification) {
      const notification = parsed.notification as Notification
      const store = useNotificationStore.getState()
      store.addNotification(notification)
      store.incrementUnreadCount()

      // Invalidate React Query cache (lazy import to avoid circular deps)
      import('@/lib/queryClient').then(({ queryClient }) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount })
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      })

      // Show toast
      const route = NOTIFICATION_ROUTES[notification.notification_type]
      toast(notification.title, {
        description: notification.body,
        duration: 5000,
        action: route
          ? { label: 'View', onClick: () => { window.location.href = route } }
          : undefined,
      })
    }
  } catch {
    // Non-JSON or unknown message shape — ignore
  }
}

function scheduleReconnect() {
  if (isIntentionalClose) return
  if (reconnectAttempts >= MAX_RETRIES) return

  const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectAttempts), MAX_DELAY)
  reconnectAttempts++
  reconnectTimer = setTimeout(async () => {
    if (isIntentionalClose) return
    // Always reconnect with a CURRENT (refreshed if needed) token — the
    // previous token may have expired, which is exactly why the socket closed.
    const token = await getValidAccessToken()
    if (!token) {
      // Session is truly gone (refresh failed) — stop retrying so we don't
      // hammer the server with handshakes that will only 403.
      return
    }
    currentToken = token
    doConnect(token)
  }, delay)
}

function doConnect(token: string) {
  // Guard: already open or connecting
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return
  }

  try {
    ws = new WebSocket(getWsUrl(token))

    ws.onopen = () => {
      reconnectAttempts = 0
    }

    ws.onmessage = handleMessage

    ws.onclose = () => {
      ws = null
      if (!isIntentionalClose) {
        scheduleReconnect()
      }
    }

    ws.onerror = () => {
      // onclose will fire after onerror — reconnect handled there
    }
  } catch {
    scheduleReconnect()
  }
}

/** Call after successful login. Idempotent — won't double-connect. */
export function connectNotificationSocket(accessToken: string) {
  currentToken = accessToken
  isIntentionalClose = false
  reconnectAttempts = 0
  doConnect(accessToken)
}

/** Call on logout. Stops reconnects. */
export function disconnectNotificationSocket() {
  isIntentionalClose = true
  currentToken = null
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (ws) {
    ws.close()
    ws = null
  }
}

/** Send a JSON message over the notification socket (e.g., mark_read). */
export function sendNotificationMessage(data: Record<string, unknown>) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data))
  }
}

/** Check if socket is currently open. */
export function isNotificationSocketConnected(): boolean {
  return ws != null && ws.readyState === WebSocket.OPEN
}
